import React, { useRef, useState, useEffect, CSSProperties } from "react";
import "./GlowDiv.css"; // Ensure this CSS file includes the necessary styles
import { Box } from '@mui/material'; // MUI Box for `sx`

interface FeatureDivItemProps {
  title: String;
  description: String;
}

interface GlowDivProps {
  children: React.ReactNode;
}

export const FeatureDivItem: React.FC<FeatureDivItemProps> = ({
  title,
  description,
}) => {
  return (
    <Box className="feature">
      <Box
        className="feature-title"
        sx={{
          fontSize: {
            xs: 'clamp(20px, 5vw, 72px)',
            sm: 'clamp(24px, 6vw, 96px)',
            md: 'clamp(32px, 7vw, 112px)',
            lg: 'clamp(40px, 8vw, 128px)',
          },
          fontWeight: 'bold',
        }}
      >
        {title}
      </Box>
      <Box
        className="feature-description"
        sx={{
          fontSize: {
            xs: 'clamp(14px, 4vw, 28px)',
            sm: 'clamp(16px, 3.5vw, 36px)',
            md: 'clamp(18px, 3vw, 48px)',
            lg: 'clamp(20px, 2.6vw, 56px)',
          },
          fontWeight: 500,
        }}
      >
        {description}
      </Box>
    </Box>
  );
};

const GlowDiv: React.FC<GlowDivProps> = ({ children }) => {
  const featuresRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [glowSize, setGlowSize] = useState({ width: 100, height: 100 }); // Default size

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (featuresRef.current) {
        const rect = featuresRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    const updateGlowSize = () => {
      if (featuresRef.current) {
        const width = featuresRef.current.offsetWidth;
        const height = featuresRef.current.offsetHeight;
        // Assuming you want the glow to be a certain percentage of the GlowDiv size, for example, 50%
        const size = Math.min(width, height) * 1.8;
        setGlowSize({ width: size, height: size });
      }
    };

    // Update the glow size on mount and whenever the window is resized
    updateGlowSize();
    window.addEventListener('resize', updateGlowSize);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener('resize', updateGlowSize);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const glowStyle = {
    position: "absolute",
    left: `${mousePosition.x - glowSize.width / 2}px`,
    top: `${mousePosition.y - glowSize.height / 2}px`,
    width: `${glowSize.width}px`,
    height: `${glowSize.height}px`,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(34,86,192,1) 0%, rgba(0,0,0,1) 75%)",
    pointerEvents: "none",
    zIndex: 1,
  } as React.CSSProperties;

  return (
    <Box ref={featuresRef} className="features"
    sx={{margin: { xs: '2px', sm: '7px', md: '15px', lg: '20px' }}}
    >
      <div
        className="glow-effect"
        style={glowStyle}
      />
      <div className="children-wrapper">
        {children}
      </div>
    </Box>
  );
};

export default GlowDiv;
